import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { setPhoneBackHome } from '../android/phoneBack';
import { useAndroidBackClose } from '../android/useAndroidBackClose';
import { canReceiveReturnedFocus, keepTabInside } from '../components/dialogFocus';
import { Icon } from '../components/ui/Icon';
import { Button, IconButton } from '../components/ui/primitives';
import { cx } from '../components/ui/primitives/cx';
import type { UserRole } from '../types/auth';
import { visibleBottomNavTabs, visibleNavGroups, type AppSection, type NavGroup } from './navConfig';
import { useShellTier } from './shellTier';
import { isTypingTarget, revealFocusedField, SoftKeyboardContext, useSoftKeyboard } from './softKeyboard';

export interface AppShellProps {
  activeSection: AppSection;
  role: UserRole | undefined;
  /** Presencia de Loyverse (ver `visibleNavGroups`): `null` mientras no se sabe. */
  loyverseConnected: boolean | null;
  /** ONB-007: la facturación está encendida (ver `visibleNavGroups`). Sin el dato, no se ofrece. */
  billingAvailable?: boolean;
  businessName: string;
  userName: string;
  roleName: string;
  /** La tienda desde la que se trabaja, o cuántas son; `null` si no hay ninguna. */
  storeLabel: string | null;
  initials: string;
  badges: Partial<Record<AppSection, number>>;
  isLoggingOut: boolean;
  onLogout: () => void;
  onNavigate: (section: AppSection) => void;
  /** P-085: abre la ayuda (un diálogo en la misma pantalla). Sin esta función no se dibuja el botón. */
  onHelp?: () => void;
  children: ReactNode;
}

const OPEN_GROUPS_KEY = 'n360.nav.gruposAbiertos';
// Aire, en px, que se deja al traer a la vista el destino activo dentro del menú.
const REVEAL_MARGIN = 12;

/**
 * Rediseño · Bloque B — el shell: lo que rodea a cada pantalla.
 *
 * Un solo marcado para los tres anchos; cuál se ve lo decide `shell.css` (cortes en `shellTier.ts`):
 *
 *   teléfono (< 768)     barra superior + contenido + barra inferior con las pestañas aprobadas.
 *                        «Más» abre el menú completo como capa.
 *   intermedio           riel de iconos siempre a la vista; el botón de menú lo despliega como capa.
 *   escritorio (≥ 1100)  menú completo fijo a la izquierda, sin barra superior.
 *
 * Este componente solo dibuja y coordina: no sabe de sesión ni de servidor (eso es `AppLayout`), y
 * por eso se puede montar en una prueba con datos a mano. Qué destinos ve cada rol lo decide
 * `navConfig.ts`, igual que antes; aquí no se concede ni se quita nada.
 *
 * Frontera con la piel anterior: el shell lleva la fuente y los colores del rediseño; el contenido
 * (`children`) va dentro de `.app-content` y sigue heredando de `:root` como siempre. Ninguna regla
 * de `shell.css` alcanza a lo que hay dentro.
 */
export function AppShell({
  activeSection,
  role,
  loyverseConnected,
  billingAvailable = false,
  businessName,
  userName,
  roleName,
  storeLabel,
  initials,
  badges,
  isLoggingOut,
  onLogout,
  onHelp,
  onNavigate,
  children,
}: AppShellProps) {
  const groups = visibleNavGroups(role, loyverseConnected, billingAvailable);
  const tabs = visibleBottomNavTabs(role, loyverseConnected);
  const activeLabel = groups.flatMap((group) => group.items).find((item) => item.id === activeSection)?.label;
  // Lo que no es una pestaña vive en «Más»: es ahí donde la barra dice que estás.
  const activeIsInMore = !tabs.some((tab) => tab.section === activeSection);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Quién abrió el menú («Más» o el botón de la barra superior), para devolverle el foco al cerrarlo.
  const openerRef = useRef<Element | null>(null);

  // Ola 4: grupos plegables (Configuración arranca plegada). Preferencia recordada solo como UX;
  // si la ruta activa vive dentro de un grupo plegado, se abre solo (nunca se esconde dónde estás).
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || '{}'); } catch { return {}; }
  });
  const toggleGroup = (label: string) => setOpenGroups((current) => {
    const next = { ...current, [label]: !(current[label] ?? false) };
    try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* solo preferencia */ }
    return next;
  });
  const isGroupOpen = (group: NavGroup) => {
    if (!group.collapsible) return true;
    if (group.items.some((item) => item.id === activeSection)) return true;
    return openGroups[group.label] ?? false;
  };

  const openMenu = (opener: Element) => {
    openerRef.current = opener;
    setMenuOpen(true);
  };

  // Cerrar sin haber elegido nada (Atrás, Escape, el velo, la «×»): el foco vuelve a quien abrió.
  const dismissMenu = useCallback(() => {
    setMenuOpen(false);
    const opener = openerRef.current;
    if (canReceiveReturnedFocus(opener)) opener.focus({ preventScroll: true });
  }, []);

  const go = (section: AppSection) => {
    onNavigate(section);
    if (!menuOpen) return;
    // Se eligió un destino desde el menú abierto como capa: el menú se cierra y el foco pasa a la
    // pantalla nueva, que es lo que la persona viene a leer.
    setMenuOpen(false);
    contentRef.current?.focus({ preventScroll: true });
  };

  // N4 (la parte del shell): el menú abierto es un overlay más de la pila del Atrás de Android. El
  // Atrás lo cierra antes de navegar o salir, y siempre después de lo que tenga encima.
  const isTopmost = useAndroidBackClose(dismissMenu, menuOpen, sidebarRef);

  // Bloque G: con nada abierto encima, el Atrás del teléfono va al Inicio como al tocar «Inicio» (ver
  // `phoneBack.ts`). Se activa mientras el shell está montado; siempre con la navegación más reciente.
  const navigateRef = useRef(onNavigate);
  useLayoutEffect(() => { navigateRef.current = onNavigate; });
  useEffect(() => setPhoneBackHome(() => navigateRef.current('dashboard')), []);

  // Bloque G: mientras se escribe con el teclado de la pantalla, la barra inferior se esconde (`shell.css`)
  // y el campo se trae a la vista si quedó tapado. Antes, en un teléfono, la barra quedaba encima del campo.
  // P-079: la medida lee también el `visualViewport` (Chrome Android 108+ y Firefox Android 132+ no encogen la
  // ventana) y publica lo que el teclado recorta como `--n3-keyboard-inset` en el documento: con eso la hoja
  // sube (`primitives.css`), el área que se desplaza reserva ese espacio para que un campo al final siga siendo
  // alcanzable (`shell.css`) y el pie pegajoso de un formulario sube también (`legacy-bridge.css`). Se publica en
  // el documento y no en el shell para que la lean también los diálogos montados fuera de él (la confirmación).
  const { open: keyboardOpen, inset: keyboardInset } = useSoftKeyboard();
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--n3-keyboard-inset', `${keyboardInset}px`);
    return () => { document.documentElement.style.removeProperty('--n3-keyboard-inset'); };
  }, [keyboardInset]);
  // El campo con el foco se trae a la vista en cada `focusin` de un campo de escribir —también con el teclado ya
  // arriba: el `autoFocus` de un paso nuevo— y en cada cambio del área visible mientras un campo lo tenga. Tras un
  // `requestAnimationFrame`, para medir ya con la maquetación nueva; si ya se ve entero, no se mueve nada.
  useEffect(() => {
    let frame = 0;
    const reveal = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => revealFocusedField(scrollRef.current));
    };
    const onFocusIn = (event: FocusEvent) => { if (isTypingTarget(event.target as Element | null)) reveal(); };
    const onViewport = () => { if (isTypingTarget(document.activeElement)) reveal(); };
    const viewport = window.visualViewport;
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('resize', onViewport);
    viewport?.addEventListener('resize', onViewport);
    viewport?.addEventListener('scroll', onViewport);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('resize', onViewport);
      viewport?.removeEventListener('resize', onViewport);
      viewport?.removeEventListener('scroll', onViewport);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    // Al abrir, el foco entra al menú: al contenedor, para que el primer Tab caiga en el primer destino.
    sidebarRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      // Mismo orden que el Atrás: si hay algo abierto encima, le toca a eso.
      if (!isTopmost()) return;
      if (event.key === 'Escape') dismissMenu();
      if (event.key === 'Tab' && sidebarRef.current && keepTabInside(sidebarRef.current, event.shiftKey)) event.preventDefault();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, isTopmost, dismissMenu]);

  // Si la ventana cambia de ancho (girar el teléfono, redimensionar), un menú abierto como capa se
  // cierra: en el ancho nuevo puede no ser una capa, y no debe quedar un velo sin menú.
  const tier = useShellTier();
  const lastTier = useRef(tier);
  useEffect(() => {
    if (lastTier.current === tier) return;
    lastTier.current = tier;
    if (!menuOpen) return;
    setMenuOpen(false);
    // Lo que tenía el foco puede no verse en el ancho nuevo (la «×» y «Cerrar sesión» no existen en el
    // riel; en teléfono el menú cerrado se oculta entero), y quien lo abrió tampoco: «Más» y el botón
    // de menú son de un solo ancho. El foco pasa a la pantalla, que está en los tres.
    if (sidebarRef.current?.contains(document.activeElement)) contentRef.current?.focus({ preventScroll: true });
  }, [tier, menuOpen]);

  // El documento deja de desplazarse mientras el shell está montado: lo hace el área de contenido,
  // para que la barra inferior quede debajo de ella y no encima (ver `shell.css`).
  useLayoutEffect(() => {
    document.documentElement.classList.add('n3-shell-host');
    return () => document.documentElement.classList.remove('n3-shell-host');
  }, []);

  // Cada pantalla empieza arriba: el área que se desplaza es la misma para todas.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeSection]);

  // El cuerpo del menú tiene su propio desplazamiento (ver `shell.css`): el destino en el que estás
  // se trae a la vista, también al abrir el menú, para que nunca haya que buscarlo.
  useEffect(() => {
    const body = bodyRef.current;
    const current = body?.querySelector('[aria-current="page"]');
    if (!body || !current) return;
    const visible = body.getBoundingClientRect();
    const item = current.getBoundingClientRect();
    if (item.top < visible.top) body.scrollTop -= visible.top - item.top + REVEAL_MARGIN;
    else if (item.bottom > visible.bottom) body.scrollTop += item.bottom - visible.bottom + REVEAL_MARGIN;
  }, [activeSection, menuOpen, tier]);

  const identity = `${roleName}${storeLabel ? ` · ${storeLabel}` : ''}`;

  return (
    <div className={cx('n3-shell', menuOpen && 'n3-shell--menu-open', keyboardOpen && 'n3-shell--keyboard')}>
      {/* Un <div> y no un <aside>: abierto como capa es un diálogo, y ese papel no se le puede dar a un
          <aside>. En reposo no necesita papel propio: la navegación de dentro ya es un punto de referencia. */}
      <div
        aria-label={menuOpen ? 'Menú' : undefined}
        aria-modal={menuOpen || undefined}
        className="n3-shell__sidebar"
        id={menuId}
        ref={sidebarRef}
        role={menuOpen ? 'dialog' : undefined}
        tabIndex={-1}
      >
        {/* Lo que se desplaza: todo menos el pie. Así, por baja que sea la ventana o largos que sean
            los nombres del negocio y de la tienda, cada destino se alcanza desplazando. */}
        <div className="n3-shell__sidebar-body" ref={bodyRef}>
          <div className="n3-shell__sidebar-head">
            <Wordmark className="n3-shell__sidebar-brand" />
            <span aria-hidden="true" className="n3-shell__brand-mini">N</span>
            <IconButton className="n3-shell__close" label="Cerrar menú" onClick={dismissMenu} variant="soft"><Icon name="close" size={18} /></IconButton>
          </div>

          {/* Dónde estás trabajando. Con varias tiendas no se elige una por la persona: se dice cuántas son. */}
          <div className="n3-shell__context">
            <span aria-hidden="true" className="n3-shell__context-icon"><Icon name="stores" size={20} /></span>
            <span className="n3-shell__context-text">
              <strong className="n3-shell__context-name">{businessName}</strong>
              {storeLabel && <span className="n3-shell__context-store">{storeLabel}</span>}
            </span>
          </div>

          <nav aria-label="Navegación principal" className="n3-shell__nav">
            {groups.map((group) => {
              const open = isGroupOpen(group);
              const itemsId = `${menuId}-${group.label}`;
              return (
                <div className="n3-shell__group" key={group.label}>
                  {group.collapsible ? (
                    <button aria-controls={itemsId} aria-expanded={open} className="n3-shell__group-toggle" onClick={() => toggleGroup(group.label)} title={group.label} type="button">
                      <span aria-hidden="true" className="n3-shell__group-toggle-icon"><Icon name="settings" size={20} /></span>
                      <span className="n3-shell__group-toggle-label">{group.label}</span>
                      <span aria-hidden="true" className="n3-shell__group-toggle-chevron"><Icon name="chevron-down" size={16} /></span>
                    </button>
                  ) : (
                    <span className="n3-shell__group-label">{group.label}</span>
                  )}
                  <div className="n3-shell__group-items" id={itemsId}>
                    {open && group.items.map((item) => (
                      <button
                        aria-current={activeSection === item.id ? 'page' : undefined}
                        className={cx('n3-shell__nav-item', item.emphasis && 'n3-shell__nav-item--emphasis')}
                        key={item.id}
                        onClick={() => go(item.id)}
                        title={item.label}
                        type="button"
                      >
                        <span aria-hidden="true" className="n3-shell__nav-icon"><Icon name={item.icon} size={item.emphasis ? 18 : 20} /></span>
                        <span className="n3-shell__nav-label">{item.label}</span>
                        {badges[item.id] ? <span aria-label={`${badges[item.id]} pendientes`} className="n3-shell__nav-badge">{badges[item.id]}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Quién eres y «Cerrar sesión»: el pie, siempre a la vista. Por eso su altura está acotada:
            el nombre ocupa como mucho tres renglones (ver `shell.css`) y entero queda en `title`. */}
        <div className="n3-shell__sidebar-foot">
          <div className="n3-shell__user">
            <span aria-hidden="true" className="n3-shell__avatar">{initials}</span>
            <span className="n3-shell__user-text">
              <strong className="n3-shell__user-name" title={userName}>{userName}</strong>
              <span className="n3-shell__user-role">{roleName}</span>
            </span>
          </div>
          {onHelp && (
            <Button className="n3-shell__help" fullWidth iconStart={<Icon name="help" size={18} />} onClick={onHelp} variant="ghost">
              Ayuda
            </Button>
          )}
          <Button className="n3-shell__logout" fullWidth iconStart={<Icon name="logout" size={18} />} loading={isLoggingOut} loadingLabel="Saliendo…" onClick={onLogout} variant="ghost">
            Cerrar sesión
          </Button>
        </div>
      </div>
      <div className="n3-shell__scrim" onClick={dismissMenu} role="presentation" />

      <div className="n3-shell__main">
        <div className="n3-shell__scroll" ref={scrollRef}>
          <header className="n3-shell__topbar">
            <IconButton aria-controls={menuId} aria-expanded={menuOpen} className="n3-shell__menu-button" label="Abrir menú" onClick={(event) => openMenu(event.currentTarget)}>
              <Icon name="menu" size={22} />
            </IconButton>
            <Wordmark className="n3-shell__topbar-brand" compact />
            {/* Quién eres y desde dónde trabajas, en todas las pantallas: es lo que evita capturar un
                gasto en la tienda equivocada. Si no cabe, lo que se recorta es el texto, no la franja. */}
            <div className="n3-shell__who">
              <strong className="n3-shell__who-name" title={userName}>{userName}</strong>
              <span className="n3-shell__who-role" title={identity}>{identity}</span>
            </div>
            <span aria-hidden="true" className="n3-shell__avatar n3-shell__avatar--raised">{initials}</span>
          </header>
          <main className="n3-shell__content app-content" ref={contentRef} tabIndex={-1}>
            {/* Bloque G: las pantallas saben si el teclado está abierto, con la misma medida del shell. */}
            <SoftKeyboardContext.Provider value={keyboardOpen}>{children}</SoftKeyboardContext.Provider>
          </main>
        </div>

        <nav aria-label="Navegación rápida" className="n3-shell__bottom-nav">
          {tabs.map((tab) => (
            <button aria-current={activeSection === tab.section ? 'page' : undefined} className="n3-shell__tab" key={tab.id} onClick={() => go(tab.section)} type="button">
              <span aria-hidden="true" className="n3-shell__tab-pill"><Icon name={tab.icon} size={22} /></span>
              <span className="n3-shell__tab-label">{tab.label}</span>
            </button>
          ))}
          <button
            aria-controls={menuId}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            className={cx('n3-shell__tab', activeIsInMore && 'n3-shell__tab--current')}
            onClick={(event) => openMenu(event.currentTarget)}
            type="button"
          >
            <span aria-hidden="true" className="n3-shell__tab-pill"><Icon name="more" size={22} /></span>
            <span className="n3-shell__tab-label">Más</span>
            {activeIsInMore && activeLabel && <span className="n3-visually-hidden">(aquí está {activeLabel})</span>}
          </button>
        </nav>
      </div>
    </div>
  );
}

// El logotipo del diseño aprobado: «Negocio360» con el número en azul y una sonrisa debajo. ONB-001 lo
// reutiliza en las pantallas del registro (no se dibuja otra marca).
export function Wordmark({ className, compact = false }: { className?: string; compact?: boolean }) {
  return (
    <span className={cx('n3-shell__wordmark', compact && 'n3-shell__wordmark--compact', className)}>
      Negocio<b>360</b>
      <svg aria-hidden="true" className="n3-shell__wordmark-smile" focusable="false" height="10" viewBox="0 0 40 10" width="40">
        <path d="M3 2c9 8 25 8 34 0" fill="none" strokeLinecap="round" strokeWidth="3.2" />
      </svg>
    </span>
  );
}
